import { motion } from 'framer-motion';
import { SEOHead } from '../components/common/SEOHead';
import { Breadcrumbs } from '../components/common/Breadcrumbs';
import { CTASection } from '../components/home/CTASection';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const faqs = [
  {
    question: '¿Qué es la alimentación natural para perros y gatos?',
    answer: 'Es una dieta basada en ingredientes frescos y mínimamente procesados: carnes, vísceras, huesos carnosos, verduras y suplementos necesarios. Se adapta a las necesidades de cada animal, imitando lo que comerían de forma natural pero equilibrado para una vida doméstica.',
  },
  {
    question: '¿Cómo se realiza la consulta online?',
    answer: 'Después de rellenar un cuestionario previo con los datos de tu animal, nos conectamos por videollamada (Zoom, Google Meet o similar) para revisar el caso en detalle. Es una sesión de 45-60 minutos donde analizo toda la información y resolvemos dudas.',
  },
  {
    question: '¿Necesito experiencia previa con dieta natural?',
    answer: 'No. Te guío desde cero con instrucciones claras y prácticas. El plan incluye qué comprar, cómo preparar, cómo servir y cómo hacer la transición de forma segura. Estaré disponible para resolver todas tus dudas.',
  },
  {
    question: '¿Es adecuado para perros o gatos con problemas de salud?',
    answer: 'Cada caso se evalúa individualmente. Si hay patologías (digestivas, renales, alergias, etc.), el plan se ajusta específicamente. Siempre recomiendo coordinación con el veterinario de referencia y, en casos complejos, puede ser necesario su visto bueno previo.',
  },
  {
    question: '¿Cuánto tarda en verse algún cambio?',
    answer: 'Depende del animal y su punto de partida. Algunos cambios como mejora en heces o energía pueden notarse en los primeros días. Otros como mejora de piel, pelo o peso requieren varias semanas de constancia. La paciencia es clave.',
  },
  {
    question: '¿Qué incluye el plan nutricional?',
    answer: 'Menú semanal completo con ingredientes específicos, cantidades exactas según el peso de tu animal, lista de compra organizada, guía de preparación y conservación, pautas de transición día a día, y recomendaciones de suplementos si son necesarios.',
  },
  {
    question: '¿Puedo combinar alimentación natural con pienso?',
    answer: 'Es posible en algunos casos, aunque no siempre es lo ideal. Lo evaluamos juntos para asegurar que la combinación sea equilibrada y segura. Algunas situaciones (viajes, imprevistos) pueden requerir esta flexibilidad.',
  },
  {
    question: '¿Atiendes a cualquier raza o tamaño?',
    answer: 'Sí. Trabajo tanto con perros de razas pequeñas (Chihuahua, Yorkshire) como grandes (Pastor Alemán, Gran Danés), y con gatos de cualquier tipo. Cada plan se adapta completamente a las características de tu animal.',
  },
  {
    question: '¿Cómo funciona el seguimiento?',
    answer: 'Después de implementar el plan inicial, hacemos sesiones de revisión periódicas (cada 2-4 semanas al principio, luego más espaciadas). Evaluamos evolución, ajustamos cantidades y resolvemos dudas. También tendrás soporte por mensajería entre sesiones.',
  },
  {
    question: '¿Trabajas solo en Málaga?',
    answer: 'Mi base está en Málaga, pero atiendo de forma online a clientes de toda España (y otros países hispanohablantes). La consulta y el seguimiento se hacen por videollamada y mensajería, así que la ubicación no es una limitación.',
  },
  {
    question: '¿Qué diferencia hay entre BARF y dieta cocinada?',
    answer: 'BARF incluye alimentos crudos (carnes, huesos, vísceras), mientras que la dieta cocinada utiliza los mismos ingredientes pero cocidos. Ambas pueden ser válidas; la elección depende del animal, tu comodidad y otros factores que analizamos en la consulta.',
  },
  {
    question: '¿Es segura la alimentación natural para cachorros?',
    answer: 'Sí, con las adaptaciones adecuadas. Los cachorros tienen necesidades específicas de nutrientes para su crecimiento. Diseño planes específicos para ellos, con especial atención al calcio, fósforo y otros minerales críticos.',
  },
  {
    question: '¿Qué pasa si mi perro/gato no acepta la nueva comida?',
    answer: 'Es algo que contemplamos en el plan. La transición se hace gradualmente y, si hay rechazos, ajustamos ingredientes o presentación. Los animales muy selectivos requieren más paciencia, pero la mayoría terminan aceptando la dieta natural.',
  },
  {
    question: '¿Necesito comprar ingredientes especiales o caros?',
    answer: 'No necesariamente. Los ingredientes son los que encuentras en cualquier carnicería o supermercado: pollo, ternera, pescado, verduras... Te ayudo a optimizar la compra para que sea económicamente sostenible.',
  },
  {
    question: '¿Cuánto tiempo lleva preparar las comidas?',
    answer: 'Depende de cómo te organices. Muchas familias preparan las raciones de toda la semana en 1-2 horas y las congelan. Te doy trucos de batch cooking para minimizar el tiempo diario de preparación.',
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
};

export const FAQPage = () => {
  return (
    <>
      <SEOHead
        title="Preguntas frecuentes | REALimenta"
        description="Resuelve dudas sobre alimentación natural, porciones, transición, seguimiento y casos especiales."
        canonical="/faq"
        schema={faqSchema}
      />

      <section className="pt-12 pb-20 md:pb-28">
        <div className="section-container">
          <Breadcrumbs items={[{ label: 'Preguntas frecuentes' }]} />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto mb-12 text-center"
          >
            <h1 
              className="font-manrope text-4xl sm:text-5xl lg:text-6xl font-extrabold text-text-primary tracking-tighter mb-6"
              data-testid="faq-h1"
            >
              Preguntas frecuentes
            </h1>
            <p className="text-text-secondary text-lg">
              Respuestas a las dudas más comunes sobre nutrición natural y mis servicios.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="max-w-3xl mx-auto"
          >
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="card-brand border-none"
                  data-testid={`faq-item-${index}`}
                >
                  <AccordionTrigger className="text-left font-manrope font-semibold text-text-primary hover:text-brand-primary py-4 px-2 [&[data-state=open]]:text-brand-primary">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-text-secondary px-2 pb-4 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>

          {/* Contact prompt */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mt-16 p-6 bg-brand-sage/20 rounded-2xl max-w-3xl mx-auto text-center"
          >
            <p className="text-text-primary font-semibold mb-2">
              ¿No encuentras tu pregunta?
            </p>
            <p className="text-text-secondary">
              Escríbeme a través del <a href="/contacto" className="text-brand-primary hover:underline">formulario de contacto</a> y 
              te responderé lo antes posible.
            </p>
          </motion.div>
        </div>
      </section>

      <CTASection />
    </>
  );
};

export default FAQPage;
